import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { QueryClient, QueryClientProvider } from "react-query";
import { useLocation } from "react-router-dom";
import { NavigateFunction, matchPath, useNavigate } from "react-router-dom";
import { captureException } from "@sentry/react";
import {
  QueryClient as QueryClientNew,
  QueryClientProvider as QueryClientProviderNew,
} from "@tanstack/react-query";
import axios from "axios";
import { debounce } from "lodash";
import { toast } from "sonner";
import Loading from "./components/Loading";
import { getInboxParams } from "./helpers/inbox";
import {
  deleteCurrentNotificationDevice,
  registerNotificationDevice,
} from "./helpers/notifications";
import useHilosStore from "./hooks/useHilosStore";
import useUserNotificationSync from "./hooks/useUserNotificationSync";
import { BASE_API_URL, PUBLIC_ROUTES } from "./router/router";
import { ServiceWorkerConfig } from "./serviceWorkerRegistration";

export const queryClient = new QueryClient();

export const queryClientNew = new QueryClientNew();

interface HilosProviderProps {
  config: ServiceWorkerConfig;
}

const IGNORE_404_ERROR_MODULES = ["inbox", "template", "user"];

function HilosProvider({
  children,
  config,
}: PropsWithChildren<HilosProviderProps>) {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const [loading, setLoading] = useState(true);
  const navigateRef = useRef<NavigateFunction | null>(null);
  const lastAPIVersionRef = useRef<string | null>(null);
  const hasNetworkErrorRef = useRef<boolean>(false);
  const {
    session,
    resetSession,
    version,
    hasNetworkError,
    loadSession,
    setHasNetworkError,
    setPendingUpdate,
    setVersion,
    setInboxParams,
    setIsNetworkOnline,
    setUnreadNotificationCount,
  } = useHilosStore();

  const location = useLocation();

  useUserNotificationSync({
    connect: Boolean(!loading && session && session.id),
    setUnreadNotificationCount,
  });

  const handleMessageEvent = useCallback(
    ({ data }) => {
      try {
        const { type, pathname } = data;

        if (type === "navigate_to") {
          navigate(pathname);
        }
      } catch (error) {
        captureException(error);
      }
    },
    [navigate]
  );

  const handleIsNetworkOnline = useCallback(() => {
    setIsNetworkOnline(true);
  }, [setIsNetworkOnline]);

  const handleIsNetworkOffline = useCallback(() => {
    setIsNetworkOnline(false);
  }, [setIsNetworkOnline]);

  useLayoutEffect(() => {
    axios.defaults.baseURL = BASE_API_URL;
    axios.defaults.xsrfCookieName = "csrftoken";
    axios.defaults.xsrfHeaderName = "X-CSRFToken";
    axios.defaults.withCredentials = true;

    // To identify requests from our own app
    axios.defaults.headers.common["X-Origin"] = "APP";

    axios.interceptors.response.use(
      (response) => {
        if (response.headers && response.headers["api-version"]) {
          if (lastAPIVersionRef.current !== response.headers["api-version"]) {
            if (lastAPIVersionRef.current) {
              setPendingUpdate();
              toast(
                t(
                  "new-version-available.title",
                  "There's a new version available!"
                ),
                {
                  description: t(
                    "new-version-available.description",
                    "To make sure everything works as expected, please reload Hilos. Don't forget to save your work first!"
                  ),
                  action: {
                    label: t("new-version-available.refresh", "Refresh"),
                    onClick: () => {
                      // @ts-ignore
                      window.location.reload(true);
                    },
                  },
                  duration: 60000,
                  important: true,
                }
              );
            } else {
              setVersion(response.headers["api-version"]);
            }

            lastAPIVersionRef.current = response.headers["api-version"];
          }

          if (hasNetworkErrorRef.current) {
            setHasNetworkError(false);
          }
        }
        return response;
      },
      (error) => {
        if (!error.response) {
          //Network error
          error.response = {
            status: 500,
            data: {
              non_field_errors: [
                "Ocurrió un error inesperado, intenta de nuevo.",
              ],
            },
          };
        }
        if (error.toJSON && error.toJSON().message === "Network Error") {
          if (!hasNetworkErrorRef.current) {
            setHasNetworkError(true);
          }
        }

        if (error.response && error.response.status === 401) {
          resetSession();
          const { pathname } = window.location;
          const isPublicRoute = PUBLIC_ROUTES.find((element) =>
            matchPath(element, pathname)
          );
          if (!isPublicRoute && navigateRef.current) {
            let loginPath = "/login";
            if (pathname !== "/") {
              loginPath += `?next=${pathname}`;
            }
            navigateRef.current(loginPath);
          }
        }

        if (
          error.response?.status === 404 &&
          !IGNORE_404_ERROR_MODULES.includes(
            error.config?.headers?.["x-module"]
          )
        ) {
          const module = error.config?.headers?.["x-module"];

          if (module) {
            navigateRef.current?.(`/404?module=${module}`);
          }
        }

        return Promise.reject(error);
      }
    );
  }, [
    config,
    t,
    setVersion,
    setPendingUpdate,
    setHasNetworkError,
    resetSession,
  ]);

  useEffect(() => {
    if ("navigator" in window && window.navigator.onLine) {
      handleIsNetworkOnline();
    }

    window.addEventListener("online", handleIsNetworkOnline);
    window.addEventListener("offline", handleIsNetworkOffline);

    return () => {
      window.removeEventListener("online", handleIsNetworkOnline);
      window.removeEventListener("offline", handleIsNetworkOffline);
    };
  }, [handleIsNetworkOnline, handleIsNetworkOffline]);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleMessageEvent);

      return () =>
        navigator.serviceWorker.removeEventListener(
          "message",
          handleMessageEvent
        );
    }
  }, [handleMessageEvent]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    hasNetworkErrorRef.current = hasNetworkError;
  }, [hasNetworkError]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLoadSession = useCallback(
    debounce(async () => {
      const user = await loadSession();

      if (user) {
        const nextInboxParams = await getInboxParams();
        setInboxParams(nextInboxParams);
      } else {
        const { pathname } = window.location;
        const isPublicRoute = PUBLIC_ROUTES.find((element) =>
          matchPath(element, pathname)
        );

        await deleteCurrentNotificationDevice();

        if (!isPublicRoute && navigateRef.current) {
          let loginPath = "/login";
          if (pathname !== "/") {
            loginPath += `?next=${pathname}`;
          }
          navigateRef.current(loginPath);
        }
      }
      setLoading(false);
    }, 250),
    []
  );

  const handlePushyTokenResult = useCallback((deviceToken: string) => {
    registerNotificationDevice(deviceToken);
    return true;
  }, []);

  useEffect(() => {
    handleLoadSession();
  }, [handleLoadSession]);

  useEffect(() => {
    window["onPushyTokenResult"] = handlePushyTokenResult;
    return () => {
      delete window["onPushyTokenResult"];
    };
  }, [handlePushyTokenResult]);

  useLayoutEffect(() => {
    if (lastAPIVersionRef.current && version !== lastAPIVersionRef.current) {
      // @ts-ignore
      window.location.reload(true);
    }
  }, [location, version, navigate]);

  if (loading) {
    return (
      <div className="h-full w-full flex justify-center items-center">
        <Loading />
      </div>
    );
  }

  return (
    <QueryClientProviderNew client={queryClientNew}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </QueryClientProviderNew>
  );
}

export default HilosProvider;
